// src/components/Banner/SignupBanner.jsx
import React from "react";
import { Box, Paper, Typography, IconButton, Tooltip, Link } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function SignupBanner({
  // Updated default messaging to your new test variant (kept prop for flexibility)
  message = "Make this form match your brand rules and view every campaign URL in one place.",
  ctaLabel = "Start free trial",
  ctaHref = "https://app.utmsmartmanager.com/signup/",
  onClose,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderBottom: "1px solid #D3D3D3",

        // ✅ Solid very-light blue to avoid blending with lavender page background
        // (No transparency = no purple shift.)
        backgroundColor: "#E8F0FE",
      }}
      role="region"
      aria-label="Signup banner"
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.25,

          // ✅ Motivational accent (action blue)
          borderLeft: "4px solid #1A73E8",
          pl: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            // Force Roboto locally (no theme edits)
            fontFamily: "Roboto, sans-serif",

            // Slightly tighter + cleaner for a banner row
            lineHeight: 1.35,

            // Layout behavior
            flex: 1,
            minWidth: 0,

            // one line on sm+, wrap on xs
            whiteSpace: { xs: "normal", sm: "nowrap" },
            overflow: { xs: "visible", sm: "hidden" },
            textOverflow: { xs: "clip", sm: "ellipsis" },
          }}
        >
          {/* Lead-in: semibold, slightly darker */}
          <Typography
            component="span"
            sx={{
              fontFamily: "inherit",
              // ⬇️ Slightly reduced weight so the CTA doesn't feel “smaller” by comparison
              fontWeight: 500,
              color: "#202124", // ✅ Google-style primary text
            }}
          >
            Make this form match your brand rules
          </Typography>

          {/* Detail: regular, slightly softer */}
          <Typography
            component="span"
            sx={{
              fontFamily: "inherit",
              fontWeight: 400,
              color: "#3C4043", // ✅ Google-style secondary text
            }}
          >
            {" "}
            and view every campaign URL in one place.{" "}
          </Typography>

          {/* CTA: semibold, brand blue */}
          <Link
            href={ctaHref}
            underline="none"
            sx={{
              fontFamily: "inherit",

              // ✅ Force Link typography to match the surrounding text
              // Link sometimes renders with its own default typography styling;
              // explicitly inherit eliminates visual “smaller text” surprises.
              fontSize: "inherit",
              lineHeight: "inherit",

              // ✅ Make CTA feel at least as prominent as the lead-in
              fontWeight: 700,

              // ✅ Nudge size up slightly (best fix when inheritance still looks smaller)
              // This is subtle enough to keep the banner clean, but makes the CTA read as primary.
              fontSizeAdjust: "none", // avoid font metric tricks; keep predictable
              // Use a tiny relative bump that doesn't break layout
              // (still works with one-line + ellipsis behavior)
              fontSize: "1.05em",

              whiteSpace: "nowrap",
              color: "#1A73E8", // ✅ Google-style action blue

              // ✅ Hover: darker action blue (no underline)
              "&:hover": {
                color: "#1558D6",
              },

              // Optional: keep it feeling clickable without underline
              cursor: "pointer",
            }}
          >
            {ctaLabel}
          </Link>
        </Typography>

        {typeof onClose === "function" && (
          <Tooltip title="Dismiss" arrow>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Dismiss banner"
              sx={{
                borderRadius: 1,
                flexShrink: 0,
                // Keeps the icon from feeling too dark/heavy against the tint
                color: "rgba(0,0,0,0.6)",
                "&:hover": { color: "rgba(0,0,0,0.8)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
}
